import { createContext, FC, ReactNode, useContext } from 'react';
import { useMutationBulkApply } from 'src/hooks/useMutationBulkApply';
import { useMutationBulkRemove } from 'src/hooks/useMutationBulkRemove';
import { useMutationTruckShiftCopy } from 'src/hooks/useMutationTruckShiftCopy';

type ContextValue = {
  bulkApply: ReturnType<typeof useMutationBulkApply>;
  bulkRemove: ReturnType<typeof useMutationBulkRemove>;
  copyMutation: ReturnType<typeof useMutationTruckShiftCopy>;
  // 稼働表更新（一括反映/解除/コピー）のいずれかが実行中か。
  isUpdating: boolean;
};

const TruckShiftUpdateContext = createContext<ContextValue | null>(null);

// 稼働表更新の mutation（一括反映/解除/先週コピー）を App レベルで保持する Provider。
// React Query v4 では mutation が実行元コンポーネントの observer に紐づき、かつ別画面からの
// useIsMutating は実行中の mutation を検知できない（稼働表を離れると 0 になる）。そこで
// observer を画面遷移で unmount しない App レベルの Provider に持たせ、その isLoading を
// Context 経由で全画面へ配ることで、別画面でも進捗表示・編集ロックを継続させる。
// （完了時の invalidate は App の MutationCache に集約済み。）
export const TruckShiftUpdateProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const bulkApply = useMutationBulkApply();
  const bulkRemove = useMutationBulkRemove();
  const copyMutation = useMutationTruckShiftCopy();
  const isUpdating = bulkApply.isPending || bulkRemove.isPending || copyMutation.isPending;
  return (
    <TruckShiftUpdateContext.Provider value={{ bulkApply, bulkRemove, copyMutation, isUpdating }}>
      {children}
    </TruckShiftUpdateContext.Provider>
  );
};

export const useTruckShiftUpdate = (): ContextValue => {
  const ctx = useContext(TruckShiftUpdateContext);
  if (!ctx) throw new Error('useTruckShiftUpdate は TruckShiftUpdateProvider の内側で使用してください');
  return ctx;
};
